/**
 * Error-code → user copy, and the one place a `BridgeError` becomes a
 * sentence in the UI. The bridge's `message` is already user-safe; this
 * mapping replaces code-shaped wording with guidance where the code tells
 * us what to do next.
 */
import { BridgeError } from "./client.ts";
import type { BridgeErrorCode } from "./types.ts";

/**
 * Copy per protocol error code. Codes whose bridge message already reads
 * well (`bad_request` field messages) fall through to that message.
 */
const COPY: Readonly<Partial<Record<BridgeErrorCode, string>>> = {
  unauthorized: "The mail server rejected the username or password.",
  no_such_mailbox: "That folder no longer exists on the server.",
  not_found_message: "That message was deleted before it could be opened.",
  forbidden_origin: "This app is not allowed to talk to the bridge from here.",
  too_many_requests: "Too many requests — wait a moment and try again.",
  connect_failed: "Could not reach the mail server.",
  protocol_error: "The mail server sent something unintelligible.",
  timeout: "The mail server took too long to answer.",
  message_too_large: "That message is too large to send.",
};

/**
 * Produces the sentence a user should see for a failure.
 *
 * @param error - The caught value.
 * @param fallback - Copy for anything that is not a `BridgeError`.
 * @returns One user-facing sentence.
 */
export function describeFailure(error: unknown, fallback: string): string {
  if (!(error instanceof BridgeError)) {
    return fallback;
  }
  return COPY[error.code] ?? error.message;
}
